import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import {
  SUBJECT_VOCABULARY_BY_VERTICAL,
  statusTone,
  type NetworkEdge,
  type NetworkNodeKind,
  type NetworkResponse,
  type StatusTone,
  type SubjectStatus,
} from '@ni/shared';
import { subjectStatusLabel } from './copy';
import { grownScale } from './graph-zoom';

/**
 * The Network tab's whole model layer: the response folded into something
 * drawable, and the layout that places it. Nothing here touches the DOM, so
 * all of it is testable without a renderer (network-graph.spec.ts). Text is
 * measured through an injected function: the browser passes a canvas
 * measurement, and tests an estimate.
 */

/* ————————————————————————— the model ————————————————————————— */

export interface GraphNode {
  id: string;
  /** `display_name` as the server sent it — null means the entity was deleted. */
  name: string | null;
  kind: NetworkNodeKind;
  isSubject: boolean;
  unresolved?: boolean;
  /**
   * What this person is to the org, or null — for an org or event node, and for
   * a person the org holds no subject record for. The vocabulary is the org's
   * vertical's, so the word is carried and the *label* is looked up per render.
   */
  status: SubjectStatus | null;
}

export interface GraphLink {
  /** Stable per pair: the two entity ids, ordered, joined. */
  id: string;
  source: string;
  target: string;
  /** Every bundled fact's label, in server order, deduplicated. */
  labels: string[];
  /** `labels` joined for display. */
  label: string;
  /**
   * Every bundled fact on this pair, in server order and whole — the line's
   * own detail, which is what the panel reads when the line is clicked.
   *
   * Not `labels`, and not a count of them: two facts can share a word ("board
   * seat" twice, at two different times) and `labels` deduplicates, so
   * `labels.length` says how many *phrasings* the line carries while
   * `edges.length` says how many facts it stands for. `edges.length === count`
   * always; `labels.length === count` does not.
   */
  edges: NetworkEdge[];
  /** How many facts this one line stands for. */
  count: number;
  /** Any bundled fact above the lowest sensitivity (`standard`). */
  isSensitive: boolean;
  /** Any bundled fact the server classed `severe`. */
  isSevere: boolean;
}

export interface GraphModel {
  subjectId: string;
  nodes: GraphNode[];
  links: GraphLink[];
  /** Edges the response referenced but could not place — a count, for tests. */
  droppedEdges: number;
}

/**
 * The lowest value of the shared `Sensitivity` enum (`SENSITIVITIES` in
 * shared/src/vocab.ts is `['standard', 'sensitive']`). Anything above it earns
 * the dashed stroke and the badge, so this is written as "not the floor"
 * rather than "is sensitive": a third value added later reads as sensitive
 * here without a change.
 */
export const SENSITIVITY_FLOOR = 'standard';

/** Drawing order, and the order the seed ring is laid out in. */
const KIND_ORDER: Record<NetworkNodeKind, number> = { person: 0, org: 1, event: 2 };

/** Nulls sort last; otherwise plain locale-free comparison, so tests are stable. */
function byKindThenName(a: GraphNode, b: GraphNode): number {
  const kind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  if (kind !== 0) return kind;
  if (a.name === null && b.name === null) return a.id < b.id ? -1 : 1;
  if (a.name === null) return 1;
  if (b.name === null) return -1;
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  return a.id < b.id ? -1 : 1;
}

/** The pair key: undirected, so `a`/`b` order in the fact never splits a bundle. */
function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Fold the response into nodes and drawn lines.
 *
 * Defensive on two counts, both of which are a broken response rather than a
 * broken record, and neither of which should take the tab down:
 *   - an edge naming an entity that is not in `nodes` is dropped (there is
 *     nowhere to draw it to);
 *   - a self-edge is dropped (a line from a node to itself is not a picture of
 *     anything).
 *
 * Facts sharing a pair are bundled into one line — a spouse who also sits on
 * the same board is one relationship drawn once, with both words on it.
 */
export function buildGraphModel(response: NetworkResponse): GraphModel {
  const nodes = new Map<string, GraphNode>();
  for (const node of response.nodes) {
    if (nodes.has(node.entity_id)) continue;
    nodes.set(node.entity_id, {
      id: node.entity_id,
      name: node.display_name,
      kind: node.kind,
      isSubject: node.is_subject,
      ...(node.unresolved ? { unresolved: true } : {}),
      status: node.status,
    });
  }

  const bundles = new Map<string, GraphLink>();
  let droppedEdges = 0;

  for (const edge of response.edges) {
    if (edge.a === edge.b || !nodes.has(edge.a) || !nodes.has(edge.b)) {
      droppedEdges += 1;
      continue;
    }

    const key = pairKey(edge.a, edge.b);
    const sensitive = edge.sensitivity !== SENSITIVITY_FLOOR;
    const severe = edge.severity === 'severe';
    const existing = bundles.get(key);

    if (existing) {
      if (!existing.labels.includes(edge.label)) existing.labels.push(edge.label);
      existing.edges.push(edge);
      existing.count += 1;
      existing.isSensitive = existing.isSensitive || sensitive;
      existing.isSevere = existing.isSevere || severe;
      continue;
    }

    // Endpoints are stored in pair order, not fact order: the line is
    // undirected, and direction — where it exists — is already in the label.
    const [source, target] = edge.a < edge.b ? [edge.a, edge.b] : [edge.b, edge.a];
    bundles.set(key, {
      id: key,
      source,
      target,
      labels: [edge.label],
      label: edge.label,
      edges: [edge],
      count: 1,
      isSensitive: sensitive,
      isSevere: severe,
    });
  }

  const links = [...bundles.values()].map((link) => ({
    ...link,
    label: joinEdgeLabels(link.labels),
  }));
  links.sort((a, b) => (a.id < b.id ? -1 : 1));

  const ordered = [...nodes.values()].sort(byKindThenName);
  const subject = ordered.find((node) => node.isSubject) ?? null;
  const rest = ordered.filter((node) => node !== subject);

  return {
    subjectId: subject?.id ?? response.subject_entity_id,
    nodes: subject ? [subject, ...rest] : rest,
    links,
    droppedEdges,
  };
}

/** Two facts on one line read as one relationship with two words for it. */
export function joinEdgeLabels(labels: readonly string[]): string {
  return labels.join(' · ');
}

/* ————————————————————————— labels ————————————————————————— */

/**
 * A node label long enough to collide with its neighbours is cut, with the
 * ellipsis counted inside `max` so the rendered string never exceeds it.
 */
export function truncateLabel(name: string, max: number): string {
  if (max <= 0) return '';
  if (name.length <= max) return name;
  if (max === 1) return '…';
  return `${name.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Above this many characters a name is set on two lines rather than cut. The
 * threshold is low enough that most two-word org names wrap, and the wrap is
 * chosen deterministically (below), so the picture stays identical on reload.
 */
const WRAP_ABOVE = 18;

/** Wrap complete names, balancing two lines when they fit without cutting words. */
export function wrapLabel(name: string, max: number, wrapAbove = WRAP_ABOVE): string[] {
  const width = Math.max(1, max);
  if (name.length <= Math.min(wrapAbove, width)) return [name];
  const middle = name.length / 2;
  const spaces = [...name.matchAll(/ /g)]
    .map((match) => match.index)
    .filter((index) => index > 0 && index < name.length - 1);
  spaces.sort((a, b) => Math.abs(a - middle) - Math.abs(b - middle));
  const split = spaces[0];
  if (split !== undefined && split <= width && name.length - split - 1 <= width) {
    return [name.slice(0, split), name.slice(split + 1)];
  }
  const lines: string[] = [];
  let remaining = name;
  while (remaining.length > width) {
    const boundary = remaining.lastIndexOf(' ', width);
    const end = boundary > 0 ? boundary : width;
    lines.push(remaining.slice(0, end));
    remaining = remaining.slice(end + (boundary > 0 ? 1 : 0));
  }
  if (remaining) lines.push(remaining);
  return lines;
}

export type NetworkFilter =
  `kind:${GraphNode['kind']}` | `status:${NonNullable<GraphNode['status']>}`;

/** Keep the focal record for context, and only real edges between visible endpoints. */
export function filterGraph(model: GraphModel, filter: NetworkFilter | null): GraphModel {
  if (!filter) return model;
  const nodes = model.nodes.filter(
    (node) =>
      node.isSubject ||
      filter === `kind:${node.kind}` ||
      (node.kind === 'person' && filter === `status:${node.status}`),
  );
  const ids = new Set(nodes.map((node) => node.id));
  return {
    ...model,
    nodes,
    links: model.links.filter((link) => ids.has(link.source) && ids.has(link.target)),
  };
}

/* ————————————————————————— names and status ————————————————————————— */

/**
 * The longest a node label is allowed to be, in characters, per line of the
 * wrap. The footprint below is measured from the wrapped lines, so a name is
 * given exactly the ground it is drawn in.
 */
export const NODE_LABEL_MAX = 30;

/**
 * The subject's own name is the page's title as much as a node label. It sits
 * at the centre and is never wrapped, so it is cut far later than the ring's —
 * in practice never: the cap exists only so a corrupt or pathological value
 * cannot run off the canvas.
 */
const SUBJECT_LABEL_MAX = 40;

/**
 * The lines a node's label is actually drawn on. The subject stays on one line
 * whatever its length: it is the page's own title, and a second line there
 * would land straight on its own spokes.
 */
export function nodeLabelLines(node: GraphNode): string[] {
  const label = node.name === null ? 'Deleted' : node.name;
  if (node.isSubject) return [truncateLabel(label, SUBJECT_LABEL_MAX)];
  return wrapLabel(label, NODE_LABEL_MAX);
}

/**
 * The word under the name: what this person is to the org, in the org's own
 * vocabulary. Null for every node that has no status, and null again when this
 * build carries no word for the one it was sent — `subjectStatusLabel`'s rule,
 * not a second one (`copy.ts`: absence is a no-op, never a fabricated default).
 */
export function nodeStatusLabel(node: GraphNode, vertical: string): string | null {
  if (node.status === null) return null;
  return subjectStatusLabel(vertical, node.status);
}

/**
 * The tone a tracked person's mark is filled in (`statusTone`, @ni/shared), or
 * null for anyone the org holds no record for. The same table colors the People
 * table's dot and the record's status control, so a member is one color
 * everywhere. A word this build has no tone for reads as `known`, which is the
 * table's own fallback: the mark stays filled, because the person is tracked.
 */
export function nodeTone(node: GraphNode, vertical: string): StatusTone | null {
  return node.status === null ? null : statusTone(vertical, node.status);
}

/**
 * The statuses actually on this graph, in the vertical's own vocabulary order —
 * the legend's rows. A status nobody on the canvas holds gets no swatch: a key
 * to colors that are not in the picture is a key to nothing.
 *
 * Ordered by the vocabulary rather than by first appearance, so the legend does
 * not reshuffle itself when a node is added; an unregistered vertical has no
 * order to read and so lists nothing, which is the same answer its labels give.
 */
export function statusesPresent(model: GraphModel, vertical: string): SubjectStatus[] {
  const held = new Set(model.nodes.flatMap((node) => (node.status === null ? [] : [node.status])));
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical]?.statuses ?? [];
  return vocabulary.filter((status) => held.has(status));
}

/* ————————————————————————— geometry ————————————————————————— */

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface LayoutPoint extends Point {
  id: string;
}

/**
 * The sizes the drawing is set at, in layout units. A layout unit is a CSS pixel
 * when the settled picture fits the panel; a larger picture is fitted down
 * (`viewBoxFor`), marks, names and plates together, and still never collides.
 * Zooming grows them back toward reading size (`faceScale`).
 *
 * The type sizes here and in components/network/network.css are the same
 * numbers on purpose: the footprint is measured at these sizes, and a name drawn
 * larger than it was measured would run past its own plate.
 */
export interface Geometry {
  /** A ring name's size, and the step between its wrapped lines. */
  label: number;
  line: number;
  /** The subject's name, one line. */
  subject: number;
  /** The status word's size, and the step down to it from the name. */
  statusSize: number;
  status: number;
  /** Half-width of a ring mark and of the subject's mark. */
  r: number;
  rs: number;
  /** From the bottom of a mark to the top of its name. */
  gap: number;
  /** Breathing room around a node's footprint. */
  padX: number;
  padY: number;
  /** How close to the canvas edge a footprint may sit. */
  margin: number;
}

export const WIDE_GEOMETRY: Geometry = {
  label: 13.5,
  line: 15.5,
  subject: 18,
  statusSize: 11.5,
  status: 13,
  r: 5,
  rs: 9,
  gap: 7,
  padX: 9,
  padY: 6,
  margin: 18,
};

/** Phones: a size down, and tighter room, so a 390px column still holds a ring. */
export const PHONE_GEOMETRY: Geometry = {
  label: 12.5,
  line: 14.5,
  subject: 16,
  statusSize: 11.5,
  status: 12.5,
  r: 4.5,
  rs: 8,
  gap: 6,
  padX: 5,
  padY: 4,
  margin: 6,
};

/** The subject's mark carries a ring this far outside it. */
export const SUBJECT_RING_GAP = 5;

/** What a string is set in: a ring name, the subject's name, a status word, a line's tag. */
export type TextRole = 'name' | 'subject' | 'status' | 'tag';

/** The rendered width of a string in a role, in layout units. */
export type MeasureText = (text: string, role: TextRole, geometry: Geometry) => number;

/** The size each role is set at. The tag is fixed: it is not part of a node. */
export const TAG_SIZE = 11.5;

export function textSize(role: TextRole, geometry: Geometry): number {
  if (role === 'subject') return geometry.subject;
  if (role === 'status') return geometry.statusSize;
  if (role === 'tag') return TAG_SIZE;
  return geometry.label;
}

/**
 * A width estimate where nothing can measure type (tests, server rendering):
 * half an em a character, which is about Newsreader's and Inter's average
 * advance. The browser passes a canvas measurement instead.
 */
export const estimateText: MeasureText = (text, role, geometry) =>
  text.length * textSize(role, geometry) * 0.5;

/** A node's footprint, relative to its point: left, right, top and bottom. */
export interface Box {
  l: number;
  r: number;
  t: number;
  b: number;
}

/**
 * Everything about how one node is drawn that depends on its words: its mark's
 * size, the lines of its name, where the first baseline sits, the plate behind
 * the text, and the footprint the layout keeps clear for it. The layout and the
 * drawing both read this, so what is kept clear is exactly what is drawn.
 */
export interface NodeFace {
  r: number;
  lines: string[];
  status: string | null;
  /** The first line's baseline, below the node's point. */
  baseline: number;
  lineStep: number;
  /** From the last line of the name down to the status word. */
  statusStep: number;
  /** The backing plate behind the name and status, relative to the node's point. */
  plate: { x: number; y: number; w: number; h: number };
  box: Box;
}

export function nodeFace(
  node: GraphNode,
  status: string | null,
  geometry: Geometry,
  measure: MeasureText,
): NodeFace {
  const lines = nodeLabelLines(node);
  const role: TextRole = node.isSubject ? 'subject' : 'name';
  const nameWidth = Math.max(...lines.map((line) => measure(line, role, geometry)));
  const width = Math.max(nameWidth, status === null ? 0 : measure(status, 'status', geometry));
  const r = node.isSubject ? geometry.rs : geometry.r;
  const lineStep = node.isSubject ? geometry.subject * 1.15 : geometry.line;
  const textHeight = lines.length * lineStep + (status === null ? 0 : geometry.status);
  const top = r + geometry.gap;
  /* The subject is given extra room on every side: every spoke converges on it,
     and its ring sits outside the mark. */
  const padX = geometry.padX + (node.isSubject ? 14 : 0);
  const padY = geometry.padY + (node.isSubject ? 8 : 0);
  const half = Math.max(width / 2, r + (node.isSubject ? SUBJECT_RING_GAP : 0));
  return {
    r,
    lines,
    status,
    baseline: top + (node.isSubject ? geometry.subject * 0.92 : geometry.label * 0.9),
    lineStep,
    statusStep: geometry.status,
    plate: { x: -width / 2 - 3, y: top - 1, w: width + 6, h: textHeight + 3 },
    box: {
      l: -half - padX,
      r: half + padX,
      t: -r - padY - (node.isSubject ? SUBJECT_RING_GAP + 1 : 0),
      b: top + textHeight + padY,
    },
  };
}

/* ————————————————————————— the layout ————————————————————————— */

/**
 * Every tuned constant of the solve, so the look is one block to read.
 *
 * The physics is d3-force's: springs along the ties, charge between every pair
 * and collision around each name, settled from a ring. That is what draws a
 * record as clusters — two of the subject's connections who know each other sit
 * together, because a closure chord is shorter than a spoke. Do not scale the
 * forces to the panel to make the drawing fill it (the redesign's first solver
 * did, and every graph came out evenly spread to the edges); the view is fitted
 * to whatever shape the solve settles in instead (`viewBoxFor`).
 */
export const FORCE = {
  /** The canvas the solve runs on. Only its middle matters: the view is fitted after. */
  width: 900,
  height: 620,
  /** The seed ring's radius, as a share of the canvas's shorter side. */
  seedRadius: 0.36,
  /** Spring length from the subject to a connection. */
  linkDistance: 150,
  /** Each extra fact on a line pulls the pair a little closer. */
  linkDistancePerExtraFact: -10,
  /** A tie between two of the subject's connections wants to be shorter than a spoke. */
  closureDistance: 105,
  linkStrength: 0.32,
  charge: -600,
  chargeDistanceMax: 600,
  /** Room kept around a footprint's collision circle (`collideRadius`). */
  collidePadding: 4,
  collideStrength: 0.9,
  /** A gentle pull to the middle, so the ring does not drift off the canvas. */
  centerStrength: 0.05,
  /** A fixed tick budget, with alpha decaying to its floor over exactly these ticks. */
  ticks: 400,
  /** Ticks re-run per pointer move while a node is dragged, and at what heat. */
  dragTicks: 24,
  dragHeat: 0.3,
} as const;

/**
 * Deterministic starting coordinates: the subject at dead centre, everyone else
 * on one ring around it in the model's own order (kind, then name), starting at
 * twelve o'clock and going clockwise.
 *
 * The read is one hop plus its closure, so by construction there is no second
 * ring — every non-subject node is a neighbour of the subject. Seeding the ring
 * rather than letting d3 place nodes by phyllotaxis is part of what makes the
 * settled layout identical on every reload.
 */
export function seedPositions(model: GraphModel, size: Size): Map<string, Point> {
  const cx = size.width / 2;
  const cy = size.height / 2;
  const radius = Math.min(size.width, size.height) * FORCE.seedRadius;
  const seeded = new Map<string, Point>();
  const ring = model.nodes.filter((node) => node.id !== model.subjectId);

  seeded.set(model.subjectId, { x: cx, y: cy });
  ring.forEach((node, index) => {
    const angle = -Math.PI / 2 + (index / Math.max(ring.length, 1)) * Math.PI * 2;
    seeded.set(node.id, { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
  });
  return seeded;
}

/**
 * The collision circle a footprint earns: it reaches the box's farthest side
 * from the node's point, so a long name is given its width rather than the
 * mark's. A circle cannot cover a box's corners; the box pass after the solve
 * (`createLayout`) settles those.
 */
export function collideRadius(box: Box): number {
  return Math.max(-box.l, box.r, -box.t, box.b) + FORCE.collidePadding;
}

/**
 * A tiny seeded generator. d3-force reaches for randomness in one place — two
 * bodies on the same coordinate — and that one place is enough to make a static
 * layout differ on a reload, so it is never `Math.random`.
 */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1_664_525 + 1_013_904_223) >>> 0;
    return state / 4_294_967_296;
  };
}

interface Body extends SimulationNodeDatum {
  id: string;
  x: number;
  y: number;
  box: Box;
  radius: number;
  isSubject: boolean;
}

interface Tie extends SimulationLinkDatum<Body> {
  spoke: boolean;
  count: number;
}

/** A rectangle in layout units. */
export interface Extent {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutOptions {
  /** `org.vertical`, for the status word each tracked person's footprint holds. */
  vertical: string;
  geometry?: Geometry;
  measure?: MeasureText;
}

export interface Layout {
  /** The settled picture's footprints and margin: what the view is fitted to. */
  extent: Extent;
  geometry: Geometry;
  faces: ReadonlyMap<string, NodeFace>;
  /** Positions as they stood the moment the solve finished. */
  settled: LayoutPoint[];
  /** Positions right now — after a drag has moved a node. */
  points(): LayoutPoint[];
  /**
   * Hold a node where the pointer is, inside the extent, and let the rest of
   * the picture answer: its ties pull its neighbours after it and the others
   * make room. The subject never moves.
   */
  move(id: string, x: number, y: number): void;
  /** Let go of a held node: it rejoins the forces rather than staying pinned. */
  release(id: string): void;
}

/**
 * Place every node, **synchronously and deterministically**: the same model in
 * the same panel shape is the same picture on every visit, and the first paint
 * is the settled one.
 *
 * Two steps. The simulation (`FORCE`) settles from the seed ring over a fixed
 * tick budget with a seeded random source; collision there is a circle sized
 * from each name's footprint. Then a light pass works on the footprints
 * themselves: boxes that still overlap are pushed apart, and any name a line
 * runs through is moved off it, so a highlighted tie never crosses a third
 * node's name. Names apart has the last word, since two names on top of each
 * other is the worse failure.
 *
 * `size` is the panel's. Only its shape is read: the pull to the middle is
 * split between the axes so a wide panel draws a wider picture and a phone a
 * taller one, without stretching anything to the edges.
 */
export function createLayout(model: GraphModel, size: Size, options: LayoutOptions): Layout {
  const geometry = options.geometry ?? WIDE_GEOMETRY;
  const measure = options.measure ?? estimateText;
  const faces = new Map(
    model.nodes.map((node) => [
      node.id,
      nodeFace(node, nodeStatusLabel(node, options.vertical), geometry, measure),
    ]),
  );
  const seeded = seedPositions(model, FORCE);
  const cx = FORCE.width / 2;
  const cy = FORCE.height / 2;

  const bodies: Body[] = model.nodes.map((node) => {
    const at = seeded.get(node.id) ?? { x: cx, y: cy };
    const box = faces.get(node.id)!.box;
    return {
      id: node.id,
      x: at.x,
      y: at.y,
      box,
      radius: collideRadius(box),
      isSubject: node.isSubject,
      // The subject is pinned for good: it is what the page is about, and a
      // graph whose centre wanders is harder to read on every visit.
      ...(node.isSubject ? { fx: at.x, fy: at.y } : {}),
    };
  });
  const byId = new Map(bodies.map((body) => [body.id, body]));
  const ties = model.links.flatMap((link) => {
    const a = byId.get(link.source);
    const b = byId.get(link.target);
    if (!a || !b) return [];
    return [{ a, b, spoke: a.isSubject || b.isSubject, count: link.count }];
  });

  const aspect = layoutAspect(size);
  const simulation = forceSimulation<Body, Tie>(bodies)
    .randomSource(seededRandom(0x5eed))
    .alphaDecay(1 - Math.pow(0.001, 1 / FORCE.ticks))
    .force(
      'link',
      forceLink<Body, Tie>(
        ties.map(({ a, b, spoke, count }) => ({ source: a, target: b, spoke, count })),
      )
        .distance((tie) =>
          Math.max(
            60,
            (tie.spoke ? FORCE.linkDistance : FORCE.closureDistance) +
              (tie.count - 1) * FORCE.linkDistancePerExtraFact,
          ),
        )
        .strength(FORCE.linkStrength),
    )
    .force(
      'charge',
      forceManyBody<Body>().strength(FORCE.charge).distanceMax(FORCE.chargeDistanceMax),
    )
    .force('collide', forceCollide<Body>((body) => body.radius).strength(FORCE.collideStrength))
    .force('center', forceCenter<Body>(cx, cy).strength(1))
    .force('x', forceX<Body>(cx).strength(FORCE.centerStrength / Math.sqrt(aspect)))
    .force('y', forceY<Body>(cy).strength(FORCE.centerStrength * Math.sqrt(aspect)))
    .stop();
  simulation.tick(FORCE.ticks);
  /* Centring is for the settle. Left on, a drag would shift every other node
     away from the one being dragged, against its neighbors following it. */
  simulation.force('center', null);

  /* The node under the reader's pointer, held where they put it. */
  let held: Body | null = null;
  const fixed = (body: Body): boolean => body.isSubject || body === held;
  /* Unbounded while settling; the settled extent once there is one, so a drag
     never carries a name out of the view. */
  let bounds: Extent | null = null;

  const contain = (body: Body) => {
    if (fixed(body) || bounds === null) return;
    body.x = clamp(
      body.x,
      bounds.x - body.box.l,
      Math.max(bounds.x - body.box.l, bounds.x + bounds.w - body.box.r),
    );
    body.y = clamp(
      body.y,
      bounds.y - body.box.t,
      Math.max(bounds.y - body.box.t, bounds.y + bounds.h - body.box.b),
    );
  };

  /** Push overlapping boxes apart along the shorter overlap. True if any overlapped. */
  const collide = (): boolean => {
    let any = false;
    for (let i = 0; i < bodies.length; i += 1) {
      for (let j = i + 1; j < bodies.length; j += 1) {
        const p = bodies[i]!;
        const q = bodies[j]!;
        const ox = Math.min(p.x + p.box.r, q.x + q.box.r) - Math.max(p.x + p.box.l, q.x + q.box.l);
        const oy = Math.min(p.y + p.box.b, q.y + q.box.b) - Math.max(p.y + p.box.t, q.y + q.box.t);
        if (ox <= 0 || oy <= 0) continue;
        if (fixed(p) && fixed(q)) continue;
        any = true;
        const [sp, sq] = fixed(p) ? [0, 1] : fixed(q) ? [1, 0] : [0.5, 0.5];
        if (ox < oy) {
          const shift = (q.x >= p.x ? 1 : -1) * (ox + 0.5);
          p.x -= shift * sp;
          q.x += shift * sq;
        } else {
          const shift = (q.y >= p.y ? 1 : -1) * (oy + 0.5);
          p.y -= shift * sp;
          q.y += shift * sq;
        }
      }
    }
    return any;
  };

  /**
   * Move every name a line runs through off that line. A line belongs to its
   * two ends; any third footprint it crosses is pushed square off it, to the
   * side its centre is already on. The subject does not move, so a chord through
   * the centre moves its two ends outward instead. True if anything crossed.
   */
  const clearLines = (): boolean => {
    let any = false;
    for (const tie of ties) {
      for (const body of bodies) {
        if (body === tie.a || body === tie.b) continue;
        const push = pushOffLine(tie.a, tie.b, body);
        if (push === null) continue;
        any = true;
        /* The name and the line each give half: the name steps aside, and the
           line is moved the other way. A line hanging off the subject pivots on
           it, so its far end moves by the lever that puts the half-step at the
           name's own distance along it. */
        const share = fixed(body) ? 0 : 0.5;
        body.x += push.x * share;
        body.y += push.y * share;
        contain(body);
        const rest = 1 - share;
        const pivot = fixed(tie.a) ? tie.a : fixed(tie.b) ? tie.b : null;
        if (pivot === null) {
          for (const end of [tie.a, tie.b]) {
            end.x -= push.x * rest;
            end.y -= push.y * rest;
            contain(end);
          }
        } else {
          const far = pivot === tie.a ? tie.b : tie.a;
          if (fixed(far)) continue;
          const length2 = (far.x - pivot.x) ** 2 + (far.y - pivot.y) ** 2 || 1;
          const along =
            ((body.x - pivot.x) * (far.x - pivot.x) + (body.y - pivot.y) * (far.y - pivot.y)) /
            length2;
          const lever = 1 / clamp(along, 0.25, 1);
          far.x -= push.x * rest * lever;
          far.y -= push.y * rest * lever;
          contain(far);
        }
      }
    }
    return any;
  };

  const separate = (passes: number) => {
    for (let pass = 0; pass < passes; pass += 1) {
      if (!collide()) return;
      bodies.forEach(contain);
    }
  };

  /* The light step: the pair loops are quadratic, so a crowded event's few
     hundred attendees get fewer passes than a record's thirty. */
  const small = bodies.length <= 80;
  separate(small ? 400 : 120);
  const linePasses = small ? 200 : 8;
  for (let pass = 0; pass < linePasses; pass += 1) {
    const crossed = clearLines();
    let overlapped = false;
    for (let settle = 0; settle < 20; settle += 1) {
      if (!collide()) break;
      overlapped = true;
    }
    if (!crossed && !overlapped) break;
  }
  separate(400);

  bounds = footprintExtent(bodies, geometry.margin);
  const extent = bounds;

  const points = (): LayoutPoint[] => bodies.map((body) => ({ id: body.id, x: body.x, y: body.y }));

  /* A drag reheats the same simulation for a short burst, then settles names
     apart inside the extent. */
  const answer = () => {
    simulation.alpha(Math.max(simulation.alpha(), FORCE.dragHeat));
    simulation.tick(FORCE.dragTicks);
    bodies.forEach(contain);
    separate(DRAG_SEPARATE);
  };

  return {
    extent,
    geometry,
    faces,
    settled: points(),
    points,
    move(id, x, y) {
      const body = byId.get(id);
      if (!body || body.isSubject) return;
      held = body;
      body.fx = body.x = clamp(
        x,
        extent.x - body.box.l,
        Math.max(extent.x - body.box.l, extent.x + extent.w - body.box.r),
      );
      body.fy = body.y = clamp(
        y,
        extent.y - body.box.t,
        Math.max(extent.y - body.box.t, extent.y + extent.h - body.box.b),
      );
      answer();
    },
    release(id) {
      if (held?.id !== id) return;
      held.fx = null;
      held.fy = null;
      held = null;
      answer();
    },
  };
}

/**
 * The only thing the solve reads from the panel: its shape, clamped, and
 * rounded so a resize that keeps the shape does not solve the picture again.
 */
export function layoutAspect(size: Size): number {
  const aspect = size.height > 0 ? size.width / size.height : 1;
  return Math.round(clamp(aspect, 0.5, 2) * 20) / 20;
}

/** Collision passes after each drag move, so names never overlap mid-drag. */
const DRAG_SEPARATE = 40;

/** The box every footprint sits in, with a margin. */
function footprintExtent(bodies: readonly Body[], margin: number): Extent {
  if (bodies.length === 0) {
    return {
      x: FORCE.width / 2 - margin,
      y: FORCE.height / 2 - margin,
      w: margin * 2,
      h: margin * 2,
    };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const body of bodies) {
    minX = Math.min(minX, body.x + body.box.l);
    minY = Math.min(minY, body.y + body.box.t);
    maxX = Math.max(maxX, body.x + body.box.r);
    maxY = Math.max(maxY, body.y + body.box.b);
  }
  return {
    x: minX - margin,
    y: minY - margin,
    w: maxX - minX + margin * 2,
    h: maxY - minY + margin * 2,
  };
}

/** `createLayout` when only the coordinates are wanted. */
export function runLayout(model: GraphModel, size: Size, options: LayoutOptions): LayoutPoint[] {
  return createLayout(model, size, options).settled;
}

/* ————————————————————————— the view ————————————————————————— */

/** The svg's viewBox, and how many screen pixels one layout unit is drawn at. */
export interface ViewBox extends Extent {
  scale: number;
}

/**
 * The view that fits the settled picture into the panel: the extent, grown
 * about its centre to the panel's own shape so `preserveAspectRatio="meet"`
 * binds on both axes, and never smaller than the panel, so a small network is
 * drawn at its authored size rather than blown up to fill the space.
 */
export function viewBoxFor(extent: Extent, panel: Size): ViewBox {
  const width = Math.max(panel.width, 1);
  const height = Math.max(panel.height, 1);
  let w = Math.max(extent.w, width);
  let h = Math.max(extent.h, height);
  if (w / h < width / height) w = (h * width) / height;
  else h = (w * height) / width;
  return {
    x: extent.x + extent.w / 2 - w / 2,
    y: extent.y + extent.h / 2 - h / 2,
    w,
    h,
    scale: width / w,
  };
}

/**
 * The reader's zoom over the fitted view: layout point `p` is drawn at
 * `p × k + (x, y)` in viewBox units. Identity is the fit.
 */
export interface Zoom {
  k: number;
  x: number;
  y: number;
}

export const ZOOM_FIT: Zoom = { k: 1, x: 0, y: 0 };

/**
 * How far the reader may zoom, as a multiple of the fit. Not below it: names do
 * not shrink past their fitted size, so zooming out would only pile them up, and
 * a scroll down over the graph at the fit is then free to scroll the page.
 */
export const ZOOM_LIMITS = { min: 1, max: 4 } as const;

/** Keep the zoomed picture covering the view, so a pan cannot lose it off an edge. */
export function clampZoom(zoom: Zoom, view: Extent): Zoom {
  const k = clamp(zoom.k, ZOOM_LIMITS.min, ZOOM_LIMITS.max);
  /* At the fit there is nowhere to pan. */
  if (k === 1) return ZOOM_FIT;
  return {
    k,
    x: clamp(zoom.x, (view.x + view.w) * (1 - k), view.x * (1 - k)),
    y: clamp(zoom.y, (view.y + view.h) * (1 - k), view.y * (1 - k)),
  };
}

/** Zoom by `factor` about a point in viewBox units, which stays where it is. */
export function zoomAbout(zoom: Zoom, factor: number, at: Point, view: Extent): Zoom {
  const k = clamp(zoom.k * factor, ZOOM_LIMITS.min, ZOOM_LIMITS.max);
  const applied = k / zoom.k;
  return clampZoom(
    { k, x: at.x - (at.x - zoom.x) * applied, y: at.y - (at.y - zoom.y) * applied },
    view,
  );
}

/**
 * The largest a ring name is drawn on screen, in CSS pixels: the type scale's
 * 17px step (`--text-16` in styles/tokens.css), reading size in the blend spec.
 */
export const LABEL_CAP_PX = 17;

/**
 * What a node's face (mark, plate, name and status together) is multiplied by
 * in layout units at a zoom, so that on screen it grows with the zoom from its
 * fitted size up to the cap and then holds. Past the cap the positions keep
 * spreading and the type does not, which is what opens a dense cluster up.
 *
 * Never more than 1 at a zoom of 1 or more, so every footprint is at most the
 * one the layout kept clear: names stay apart and lines off them at any zoom.
 */
export function faceScale(k: number, fitScale: number, geometry: Geometry): number {
  return grownScale(fitScale * k, fitScale, LABEL_CAP_PX / geometry.label);
}

/** A footprint at a face scale, about its node's point. */
export function scaleBox(box: Box, by: number): Box {
  return { l: box.l * by, r: box.r * by, t: box.t * by, b: box.b * by };
}

/** Room a line keeps from a name it passes. */
const LINE_CLEARANCE = 2;

/**
 * Whether the segment `a`→`b` runs through `body`'s footprint, and if so the
 * shortest move square to the line that takes the footprint clear of it, toward
 * the side its centre is on. Null when the line misses.
 */
function pushOffLine(a: Point, b: Point, body: Body): Point | null {
  const left = body.x + body.box.l - LINE_CLEARANCE;
  const right = body.x + body.box.r + LINE_CLEARANCE;
  const top = body.y + body.box.t - LINE_CLEARANCE;
  const bottom = body.y + body.box.b + LINE_CLEARANCE;
  if (!segmentHitsRect(a, b, left, top, right, bottom)) return null;

  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length === 0) return null;
  const nx = -(b.y - a.y) / length;
  const ny = (b.x - a.x) / length;
  const centre = { x: (left + right) / 2, y: (top + bottom) / 2 };
  const side = (centre.x - a.x) * nx + (centre.y - a.y) * ny >= 0 ? 1 : -1;
  let nearest = Infinity;
  for (const [x, y] of [
    [left, top],
    [right, top],
    [left, bottom],
    [right, bottom],
  ] as const) {
    nearest = Math.min(nearest, side * ((x - a.x) * nx + (y - a.y) * ny));
  }
  const shift = -nearest + 0.5;
  return { x: side * nx * shift, y: side * ny * shift };
}

/** Liang–Barsky: does the segment meet the rectangle at all? */
export function segmentHitsRect(
  a: Point,
  b: Point,
  left: number,
  top: number,
  right: number,
  bottom: number,
): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  const edges: [number, number][] = [
    [-dx, a.x - left],
    [dx, right - a.x],
    [-dy, a.y - top],
    [dy, bottom - a.y],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return false;
      continue;
    }
    const t = q / p;
    if (p < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
    if (t0 > t1) return false;
  }
  return true;
}

/**
 * Where a selected line's tag sits: at the middle of the line unless a name is
 * there, and otherwise slid along it to the first place clear of every
 * footprint. A tag that finds no clear place takes the middle, where the plate
 * it is drawn on keeps it legible.
 */
export function tagPosition(
  a: Point,
  b: Point,
  tag: { w: number; h: number },
  footprints: readonly { at: Point; box: Box }[],
): Point {
  for (const t of [0.5, 0.42, 0.58, 0.34, 0.66, 0.26, 0.74]) {
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    const clear = footprints.every(
      ({ at, box }) =>
        x + tag.w / 2 < at.x + box.l ||
        x - tag.w / 2 > at.x + box.r ||
        y + tag.h / 2 < at.y + box.t ||
        y - tag.h / 2 > at.y + box.b,
    );
    if (clear) return { x, y };
  }
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high);

/* ————————————————————————— what is lit ————————————————————————— */

export interface Incident {
  /** Nodes highlighted for the active target. */
  nodes: ReadonlySet<string>;
  /** Lines highlighted for the active target. */
  links: ReadonlySet<string>;
}

export interface ActiveTarget {
  /** The node under the pointer, or the one being dragged. */
  activeNodeId: string | null;
  /** The edge under the pointer. Wins: the pointer is literally on it. */
  activeLinkId: string | null;
}

const NOTHING_LIT: Incident = { nodes: new Set(), links: new Set() };

/**
 * What the pointer is pointing at, as the two sets the view paints from.
 *
 * One rule covers both kinds of target. An edge under the pointer lights
 * itself and the two nodes it joins — the reader is asking about that
 * relationship, and its ends are the answer. A node under the pointer lights
 * every line it is an end of, and the far end of each: the reader is asking
 * who this one touches.
 *
 * The edge wins when both are set, because a pointer on a line is a more
 * specific gesture than a pointer merely near a mark.
 *
 * Empty sets mean nothing is active. The caller tests `nodes.size`.
 */
export function incidentOf(model: GraphModel, target: ActiveTarget): Incident {
  if (target.activeLinkId !== null) {
    const link = model.links.find((entry) => entry.id === target.activeLinkId);
    if (link) {
      return { links: new Set([link.id]), nodes: new Set([link.source, link.target]) };
    }
  }

  const nodeId = target.activeNodeId;
  if (nodeId === null) return NOTHING_LIT;

  const links = new Set<string>();
  const nodes = new Set<string>([nodeId]);
  for (const link of model.links) {
    if (link.source !== nodeId && link.target !== nodeId) continue;
    links.add(link.id);
    nodes.add(link.source);
    nodes.add(link.target);
  }
  return { links, nodes };
}

/* ————————————————————————— edge tooltips ————————————————————————— */

/**
 * What an edge says when the reader asks it.
 *
 * Nothing is written on the lines themselves. Thirty-odd midpoint labels over a
 * ring of names is more ink than the picture can carry — the words landed on
 * the node labels underneath, which are the part a reader actually needs — so
 * an edge's words live in a tooltip and appear when the pointer is on it. The
 * line's own shape still carries what must be visible at rest: dashed for
 * sensitive, brick for severe.
 *
 * First line is the relationship, in the server's own words, with a bundled
 * pair's two facts joined as they are everywhere else. The rest are the flags,
 * named rather than left to the dash and the colour.
 */
export function edgeTooltipText(link: GraphLink): string[] {
  const lines = [link.label];
  if (link.isSensitive) lines.push('Sensitive');
  if (link.isSevere) lines.push('Severe');
  return lines;
}
